import axios from 'axios';

/**
 * Scrape talking points and opportunities from a website using Pulpminer API
 * @param {string} website - The website URL to analyze
 * @returns {Promise<Object>} Talking points data or error
 */
export const scrapeTalkingPoints = async (website) => {
  try {
    console.log('Scraping talking points for:', website);
    
    // Ensure the website URL has protocol
    const fullWebsiteUrl = website.startsWith('http') ? website : `https://${website}`;
    
    const response = await axios.post(
      'https://tp.pressupwards.com/scrape',
      {
        url: fullWebsiteUrl
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer your-secret-api-key-12345'
        },
        timeout: 30000 // 30 second timeout
      }
    );

    console.log('Talking points API response:', response.data);

    if (response.data && response.data.website_name) {
      const talkingPointsData = response.data;
      
      return {
        success: true,
        data: {
          websiteName: talkingPointsData.website_name,
          websiteUrl: fullWebsiteUrl,
          opportunities: talkingPointsData.Opportunities || [],
          rawData: talkingPointsData
        }
      };
    } else if (response.data && response.data.error) {
      console.error('Talking points API returned error:', response.data.error);
      return {
        success: false,
        error: `API Error: ${response.data.error}`
      };
    } else {
      console.error('Unexpected talking points API response structure:', response.data);
      return {
        success: false,
        error: 'Unexpected API response structure'
      };
    }
  } catch (error) {
    console.error('Error scraping talking points:', error);
    
    if (error.response) {
      // API responded with error status
      const status = error.response.status;
      const message = error.response.data?.message || error.response.statusText;
      
      if (status === 401) {
        return {
          success: false,
          error: 'Invalid API key for talking points service'
        };
      } else if (status === 429) {
        return {
          success: false,
          error: 'Rate limit exceeded for talking points service'
        };
      } else if (status >= 500) {
        return {
          success: false,
          error: 'Talking points service temporarily unavailable'
        };
      } else {
        return {
          success: false,
          error: `Talking points API error: ${message}`
        };
      }
    } else if (error.code === 'ECONNABORTED') {
      return {
        success: false,
        error: 'Talking points request timeout'
      };
    } else if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
      return {
        success: false,
        error: 'Cannot connect to talking points service'
      };
    } else {
      return {
        success: false,
        error: `Talking points scraping failed: ${error.message}`
      };
    }
  }
};

/**
 * Process talking points data for opener generation
 * @param {Object} talkingPointsData - Raw talking points data from API
 * @returns {Object} Processed data for opener generation
 */
export const processTalkingPointsForOpeners = (talkingPointsData) => {
  if (!talkingPointsData || !talkingPointsData.success) {
    return null;
  }

  const data = talkingPointsData.data;
  
  return {
    websiteName: data.websiteName,
    opportunities: data.opportunities,
    // Format opportunities as a readable string for AI processing
    opportunitiesText: data.opportunities ? data.opportunities.join('; ') : null,
    // Create a concise talking points summary
    talkingPointsSummary: data.opportunities && data.opportunities.length > 0 
      ? `Key opportunities: ${data.opportunities.slice(0, 3).join(', ')}` 
      : null
  };
};

/**
 * Validate if a website URL is suitable for talking points scraping
 * @param {string} url - Website URL to validate
 * @returns {boolean} True if URL is valid for scraping
 */
export const isValidWebsiteForTalkingPoints = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Remove protocol and clean URL
  const cleanUrl = url.replace(/^https?:\/\//, '').trim();
  
  // Basic validation - should have at least a domain
  if (cleanUrl.length < 3 || !cleanUrl.includes('.')) {
    return false;
  }

  // Exclude common non-website URLs
  const excludePatterns = [
    /^mailto:/,
    /^tel:/,
    /^javascript:/,
    /^#/,
    /localhost/,
    /127\.0\.0\.1/,
    /^\d+\.\d+\.\d+\.\d+/ // IP addresses
  ];

  return !excludePatterns.some(pattern => pattern.test(url));
};

/**
 * Extract domain from URL for talking points scraping
 * @param {string} url - Full URL
 * @returns {string} Clean domain for API call
 */
export const extractDomainForTalkingPoints = (url) => {
  if (!url) return '';
  
  try {
    // Remove protocol
    let domain = url.replace(/^https?:\/\//, '');
    
    // Remove www. prefix
    domain = domain.replace(/^www\./, '');
    
    // Remove path, query, and fragment
    domain = domain.split('/')[0].split('?')[0].split('#')[0];
    
    return domain;
  } catch (error) {
    console.error('Error extracting domain for talking points:', error);
    return url;
  }
};